import { booleanPointInPolygon, featureCollection, } from "@turf/turf";
// import mapboxgl, { Map as MapboxMap } from "mapbox-gl";
import maplibregl, { // Formerly: MapboxGeoJSONSource
Map as MaplibreMap, } from "maplibre-gl";
import { bounceAnimation, filterPolygonRooms, } from "../components/filters/functions/polygonRooms.filter";
import { CommonFeatureStates } from "../components/layers/variables/expressions";
import { getIndoorGeoJSONProperties } from "../components/layers/properties/indoorGeoJSON.properties";
import Eventer from "./CUEventer.class";
import LayerSourceManager, { BUILDING_SOURCES, INDOORGEOJSONS_SOURCES, showObjectCondition, } from "./LayerSourceManager.class";
import GeoJSONEditor from "./CUMapEditor.class";
import DataManager from "./CUDataManager";
/**
 * Mapbox Map Extension that contains Methods and Properties relevant for CatchupUp Indoor/Outdoor
 * Orientation and Navigation
 */
export class CUMap extends MaplibreMap {
    get cuMapLoaded() {
        return this._mapLoaded;
    }
    get currentLevel() {
        return this._currentLevel;
    }
    set currentLevel(value) {
        this._currentLevel = value;
    }
    get cuContent() {
        return this._cuDataManager.cuContent;
    }
    get piData() {
        return this._cuDataManager.piData;
    }
    get campusCollection() {
        return this._cuDataManager.campusCollection;
    }
    set showBuildings(value) {
        this._cuLayerSourceManager.showBuildings = value;
    }
    set showBuildingLabels(value) {
        this._cuLayerSourceManager.showBuildingLabels = value;
    }
    set showRoomLabels(value) {
        this._cuLayerSourceManager.showRoomLabels = value;
    }
    get firstGeoJSONDataLoaded() {
        return this.cuDataManager.firstGeoJSONDataLoaded;
    }
    getGeoJSONCollection() {
        return this.cuDataManager.geoJSONCollection;
    }
    get mapStyleJSON() {
        return this.cuDataManager.mapStyleJSON;
    }
    getCuContent(type = "all") {
        return this._cuDataManager.getCuContent(type);
    }
    /**
     * SourceIds of the CU sources
     */
    get cuSourceIds() {
        return this._cuLayerSourceManager.cuSourceIds;
    }
    get orderedLayerIds() {
        return this._cuLayerSourceManager.orderedLayerIds;
    }
    /** These ones are for editing, they are only filled when an editor is making use of this class */
    get cuSourceIds_temporary() {
        return this._cuLayerSourceManager.cuSourceIds_temporary;
    }
    /**
     * LayerIds of the CU layers
     */
    get cuLayerIds() {
        return this._cuLayerSourceManager.cuLayerIds;
    }
    /**
     * LayerIds of the editing CU layers
     */
    get cuLayerIds_temporary() {
        return this._cuLayerSourceManager.cuLayerIds_temporary;
    }
    /**
     * Get the room feature for a globalPoiId
     * @deprecated Use getFeature() instead
     */
    get getRoomFeature() {
        return this.cuDataManager.getCuFeature.bind(this._cuDataManager);
    }
    /**
     * Get the room feature for a globalPoiId
     */
    get getCuFeature() {
        return this.cuDataManager.getCuFeature.bind(this._cuDataManager);
    }
    get setStyleIdsToFeatures() {
        return this.cuDataManager.setStyleIdsToFeatures.bind(this.cuDataManager);
    }
    get cuMapEditor() {
        return this._cuGeoJSONEditor;
    }
    set cuMapEditor(value) {
        this._cuGeoJSONEditor = value;
    }
    get cuDataManager() {
        return this._cuDataManager;
    }
    set cuDataManager(value) {
        this._cuDataManager = value;
    }
    constructor(cuData, options, setBadOsmIds, setLevelState, regExRoomLabel) {
        let mapUrl = cuData.mapJsonUrl;
        if (mapUrl === "piData") {
            const API_KEY = cuData.piData.produktinstanz.maplibreAccessToken;
            const mapstyleMaplibre = cuData.piData.campus[0].campusinformationen
                .mapstyleMaplibre;
            if (!API_KEY || !API_KEY.length)
                throw Error("API_KEY not set!");
            // let style = `https://api.maptiler.com/maps/openstreetmap/style.json?key=${API_KEY}`;
            mapUrl = `https://api.maptiler.com/maps/756a79b7-6f7f-46b3-bbea-160062cf649d/style.json?key=${API_KEY}`;
            if (mapstyleMaplibre && mapstyleMaplibre.length) {
                mapUrl = `${mapstyleMaplibre}/style.json?key=${API_KEY}`;
            }
        }
        super({
            ...options,
            style: mapUrl,
        });
        // export class CUMap extends MapboxMap {
        this.CU_MAPSTYLE_URL = "mapbox://styles/mapbox/streets-v11";
        this.mapSatelliteStyleURL = "mapbox://styles/mapbox/satellite-v9";
        // private popUps: { id: string; data: mapboxgl.Popup }[] = [];
        this.popUps = [];
        //@ts-ignore: will maybe used later on
        this._mapLoaded = false;
        // TODO: May be an issue with deep underground facilities!
        this.prevLevel = -999;
        this._currentLevel = 0;
        this.setLevelState = null;
        this.layerIdsRespectingLevels = [];
        this.getRealLayer = (displayValue) => {
            const allLayers = this.getStyle().layers;
            const result1 = allLayers.find((element) => {
                return (element.id = displayValue);
            });
            return result1 != null ? result1.id : "cu_rooms_geojson";
        };
        super.setStyle(mapUrl);
        this._cuEventer = new Eventer(this);
        this._cuGeoJSONEditor = new GeoJSONEditor(this, this._cuEventer, regExRoomLabel);
        this._cuLayerSourceManager = new LayerSourceManager(this, this._cuEventer);
        this._cuDataManager = new DataManager(this, this._cuEventer, cuData);
        this._cuDataManager.cuGeoJSONEditor = this._cuGeoJSONEditor;
        if (cuData.loadUnpublishedGidData) {
            console.log("Loading unpublished Gid Data.");
        }
        this.initSettings = {
            style: !!(options === null || options === void 0 ? void 0 : options.style),
            center: !!(options === null || options === void 0 ? void 0 : options.center),
        };
        this.init(setBadOsmIds);
        this.setLevelState = setLevelState;
    }
    init(setBadOsmIds) {
        console.log("MapSDK 1.0.az initializing now.");
        this._cuDataManager.init(setBadOsmIds);
        this._cuEventer.init();
        // I move this down, then every data should be loaded.
        this.runMapDependends();
    }
    /**
     * @param event
     * @param callback
     * @deprecated: Please use subscribe(...) instead!
     */
    cuOn(event, callback, key) {
        this.subscribe(event, key, callback);
    }
    /** Fires functions, which are dependend on multiple different data sources. */
    asyncDataEvents(type) {
        switch (type) {
            case "mapLoaded":
                this._cuLayerSourceManager.init();
                this._mapLoaded = true;
                this._cuDataManager.asyncDataEvents("mapLoaded");
                break;
        }
    }
    /**
     * Eventer for CUMap tailored events like listening to geojson ready/failed events. For the usual map events us this.on(...)
     * @param event Event Name
     * @param id Identifier for event to easily unsubscribe it later
     * @param cb the callback to be fired when the event pops up
     */
    get subscribe() {
        return this._cuEventer.subscribe.bind(this._cuEventer);
    }
    /**
     * Experimental unsubscribe
     * @param event Event type to unsubscribe from
     * @param id Key to identify which before subscribed cb unsubscribe from this event type
     */
    unsubscribe(event, id) {
        this._cuEventer.unsubscribe(event, id);
    }
    /** Set the popin for the building polygons and labels! */
    setBuildingZoomLevel(value) {
        // set this zoomRange so every function can make use of the new value
        this._cuLayerSourceManager.zoomRanges.buildings.end = value;
        const filter = [
            "<",
            ["zoom"],
            this._cuLayerSourceManager.zoomRanges.buildings.end,
        ];
        this.setFilter(this.cuLayerIds.buildingGeoJSONs.common, filter);
        this.setFilter(this.cuLayerIds.buildingLabels.common, filter);
    }
    setRoomZoomLevel(value) {
        const filter = [
            "all",
            [">", ["zoom"], value],
            ["!=", ["get", "name"], "kein Raumnamen gefunden"],
            showObjectCondition,
        ];
        this.setFilter(this.cuLayerIds.indoorGeoJSONs.roomsLabels, filter);
    }
    markerZoomHandler(currentZoomLevel) {
        this._cuGeoJSONEditor.markerZoomHandler(currentZoomLevel);
    }
    addLeveledLayerId(id) {
        this.layerIdsRespectingLevels.push(id);
    }
    removeLeveledLevelId(id) {
        this.layerIdsRespectingLevels = this.layerIdsRespectingLevels.filter((fId) => fId !== id);
    }
    getOrderedLayers() {
        const layerIdsForHumans = [
            "background",
            "water",
            "tunnel",
            "building",
            "road",
        ];
        const layerLength = this.getStyle().layers.length;
        const result1 = this.getStyle().layers.map((element, index) => {
            return {
                id: element.id,
                humanName: { en_EN: element.id.replace(new RegExp("_", "g"), " ") },
                index: layerLength - index,
            };
        });
        const result2 = result1.filter((element) => {
            return ((element.id.startsWith("cu_") ||
                layerIdsForHumans.includes(element.id)) &&
                !element.id.endsWith("_temp") &&
                !element.id.includes("persistent") &&
                !element.id.includes("labels"));
        });
        result2.reverse();
        return result2;
    }
    /** Refetches geojsons by request. Used when it's known, that GeoJSONs have been updated and need
     * to be displayed without reloading the app.
     */
    refetchGeoJSONs() {
        this.cuDataManager.fetchCUGeoJSONs(true);
    }
    /** Refetches PiData */
    refetchPiData() {
        this.cuDataManager.fetchPiData();
    }
    async refetchPiDataGraphQl() {
        await this.cuDataManager.fetchPiDataGraphQl();
        return true;
    }
    /** These initialisations are not dependet on any data that has to be loaded and can be
     * called right away
     */
    // ^WRONG, MK.
    runMapDependends() {
        // this one runs as the first one! When the map initializes first!
        // ^ WRONG, MK, JUST WRONG.
        clearInterval(this.mapDependendsInterval);
        // @ts-ignore
        this.mapDependendsInterval = setInterval(() => {
            if (this.loaded() && this.isStyleLoaded()) {
                this.asyncDataEvents("mapLoaded");
                clearInterval(this.mapDependendsInterval);
            }
        }, 50);
    }
    /**
     * This Function can be replaced with a custom function, that has a Feature Object as parameter.
     * When you click on a room the room is then passed to your function.
     * @param feature
     * @deprecated: use subscribe() instead!
     */
    onRoomClick(_feature) {
        // console.log(feature);
    }
    /**
     * This Function can be replaced with a custom function, that has a Feature Object as parameter.
     * When you hover on a room the room is then passed to your function.
     * @param feature
     * @deprecated: use subscribe() instead!
     */
    onRoomHover(feature) {
        return feature;
    }
    /**
     * Adds a HTML Popup to the map and manages it within the CUMap class
     * @param id
     * @param html
     * @param props
     */
    // public addPopup(id: string, props: PopUpOptions): mapboxgl.Popup {
    addPopup(id, props) {
        // const popup = new mapboxgl.Popup(props);
        const popup = new maplibregl.Popup(props);
        if (props.html) {
            popup.setHTML(props.html);
        }
        if (props.lngLat) {
            popup.setLngLat(props.lngLat);
        }
        if (props.domContent) {
            popup.setDOMContent(props.domContent);
        }
        popup.addTo(this);
        popup.on("close", () => {
            // remove when closed by hitting "x"
            this.removePopup(id, true);
        });
        this.removePopup(id, true);
        this.popUps.push({ id: id, data: popup });
        return popup;
    }
    /**
     * Updates existing Popups within the map by
     * deleting the old one with the same id and adding a new one
     * @param id
     * @param props
     */
    // public updatePopup(id: string, props: mapboxgl.PopupOptions | {} = {}): void {
    updatePopup(id, props = {}) {
        if (this.removePopup(id)) {
            this.addPopup(id, props);
        }
    }
    /**
     * Removes an existing Popup from the map
     */
    removePopup(id, suppressWarnings = false) {
        // let popup: mapboxgl.Popup | undefined;
        let popup;
        this.popUps = this.popUps.filter((p) => {
            if (p.id !== id) {
                return true;
            }
            else {
                popup = p.data;
                return false;
            }
        });
        if (popup != null) {
            popup.remove();
            return true;
        }
        else {
            if (!suppressWarnings)
                console.warn(`No PopUp with id ${id} found`);
            return false;
        }
    }
    /**
     * Returns every popup on the map with the corresponding id
     * @returns
     */
    // public getPopUps(): { id: string; data: mapboxgl.Popup }[] {
    getPopUps() {
        return this.popUps;
    }
    /**
     *
     * @returns Possible Levels for the loaded Campus
     */
    getLevels() {
        const currentCampus = this.campusCollection.getCurrentCampus();
        if (currentCampus) {
            return currentCampus.getLevels();
        }
        return [];
    }
    /**
     * This returns the "known"/"actual" level of the building. Even though technically a level is 0 (earth ground),
     * some buildings have a different presentation system where in their elevators "Level 0" might be drawn as "Level 1".
     * So this function is used for displaying a level in an interface,
     */
    getPresentationLevel(level) {
        var _a;
        const currentCampus = this.campusCollection.getCurrentCampus();
        let levelString;
        if (currentCampus && ((_a = currentCampus.floors) === null || _a === void 0 ? void 0 : _a.length)) {
            currentCampus.floors.forEach((f) => {
                if (f.stockwerk === level) {
                    levelString = f.stockwerkAnzeigeKurz;
                }
            });
        }
        if (levelString) {
            return levelString;
        }
        else {
            return null;
        }
    }
    /**
     * Sets the current floor level
     * @param level The ground floor is always level 0. Even if it's called 1 in reality.
     */
    setLevel(level) {
        // Common Etagenfilter, der gilt meist für alle
        // dieses Array wird in den Switch Cases
        // mit weiteren mapbox Expressions ("all" ist quasi AND)
        // befüllt
        const filterRasterLayers = () => {
            // herausfinden der mapboxLayerId durch serverdaten
            let rasterLayerIdOfCurrentLevel;
            const allMapLayers = this.getStyle().layers;
            const floors = this.campusCollection.getCurrentCampus().floors;
            const floorsMap = new Map();
            floors.forEach((s) => {
                // check, welches stockwerk der floors mit dem momentanen level übereinstimmt
                // UND ob es dafür KEIN geojson gibt!
                if (s.stockwerk === level && s.mapboxLayerId) {
                    rasterLayerIdOfCurrentLevel = s.mapboxLayerId;
                }
                if (s.mapboxLayerId)
                    floorsMap.set(s.mapboxLayerId, s.mapboxLayerId);
            });
            // Layout Property von allen Layern außer diesem auf hidden setzen
            // diese Operation gilt nur für den "street" Modus, wo die Catchup eigenen Raster layer vorhanden sind
            allMapLayers.forEach(({ id, type }) => {
                if (type === "raster") {
                    if (id === rasterLayerIdOfCurrentLevel) {
                        this.setLayoutProperty(id, "visibility", "visible");
                    }
                    else {
                        if (floorsMap.get(id)) {
                            // only deactivate rasters that we know, otherwise
                            // this could as well deactivate rasters that are supposed to be there
                            this.setLayoutProperty(id, "visibility", "none");
                        }
                    }
                }
            });
        };
        const baseLevelFilter = [
            "all",
            [
                "any",
                ["==", ["get", "level"], level],
                ["==", ["get", "class"], "important"],
            ],
            ["!=", ["get", "showObject"], false],
        ];
        if (level === this.prevLevel)
            return;
        const currentCampus = this.campusCollection.getCurrentCampus();
        if (!currentCampus) {
            console.warn("Campuses are not yet loaded");
            return;
        }
        if (currentCampus.hasLevel(level)) {
            this.currentLevel = level;
            this.cuMapEditor.setFloorData(this.cuDataManager.getAllGeoJSONForCampus());
            filterPolygonRooms([...baseLevelFilter], this);
            filterRasterLayers();
            bounceAnimation(this.prevLevel, level, this);
            this.prevLevel = level;
        }
        else {
            console.warn(`Level ${level} existiert nicht`);
        }
    }
    /**
     * Sets the state of either a Room or a Floor
     * @param featureId Actual Id (not globalPoiId) of the feature
     * @param stateName Name of the state
     * @param stateValue Value, that can be used to style the feature
     * @param source Mapbox sourcename
     */
    setGeoJSONFeatureState(featureId, stateName, stateValue, source) {
        const availableSources = [INDOORGEOJSONS_SOURCES, BUILDING_SOURCES];
        let foundSource = null;
        availableSources.forEach((s) => {
            if (s[source]) {
                foundSource = s[source];
            }
        });
        if (foundSource) {
            this.setFeatureState({ id: featureId, source: foundSource }, { [CommonFeatureStates[stateName]]: stateValue });
        }
        else {
            console.log("Did not find Source for: ", source);
        }
    }
    /**
     * Resets the given State on all features (of the given source) to null
     */
    resetGeoJSONFeatureState(stateName, source) {
        var _a;
        const availableSources = [INDOORGEOJSONS_SOURCES, BUILDING_SOURCES];
        let actualSourceName = null;
        availableSources.forEach((s) => {
            if (s[source]) {
                actualSourceName = s[source];
            }
        });
        if (actualSourceName != null) {
            const allMapboxSources = this.getStyle().sources;
            const sourceContent = allMapboxSources[actualSourceName];
            if (sourceContent.type === "geojson" && sourceContent.data) {
                const data = sourceContent.data;
                (_a = data.features) === null || _a === void 0 ? void 0 : _a.forEach((f) => {
                    this.setFeatureState(
                    // @ts-ignore
                    { id: f.id, source: actualSourceName }, { [stateName]: null });
                });
            }
        }
    }
    /**
     * FOR UNSUAL USE ONLY! You should always use the Main Component to pass Layers as Props!
     * This function adds the passed layer to the map object.
     */
    // public addLayer(layer: mapboxgl.AnyLayer, beforeId?: string): this {
    addLayer(layer, beforeId) {
        if (!super.getLayer(layer.id)) {
            super.addLayer(layer, beforeId);
        }
        else {
            console.warn(`Layer ${layer.id} already exists`);
        }
        return this;
    }
    /**
     * @override
     * FOR UNSUAL USE ONLY! You should always use the Main Component to pass Layers as Props!
     * Removes the passed layerId from the mapobject
     */
    removeLayer(layerId) {
        if (this.getLayer(layerId)) {
            super.removeLayer(layerId);
        }
        return this;
    }
    /**
     * @override
     * Adds a source to the Map Object. This source does not yet have features, they must be set seperately
     */
    addSource(id, source) {
        if (!this.getSource(id)) {
            super.addSource(id, source); // Muss SourceSpecification sein
        }
        else {
            console.warn(`The source ${id} already exists with the map`);
        }
        return this;
    }
    /**
     * @override
     * Removes the sources and its features
     */
    removeSource(sourceId) {
        if (this.getSource(sourceId)) {
            const layersToRemove2 = this.getStyle().layers.filter((element) => {
                // @ts-ignore
                return sourceId === element.source;
            });
            const layersToRemove3 = layersToRemove2.map((element) => {
                return element.id;
            });
            layersToRemove3.forEach((element) => {
                this.removeLayer(element);
            });
            // console.log(layersToRemove3);
            super.removeSource(sourceId);
        }
        return this;
    }
    /**
     * Sets the features for a before declared Source
     */
    setFeatures(sourceId, data) {
        if (!data) {
            console.warn("No Features passed, for emptying the source pass []");
            return;
        }
        const source = this.getSource(sourceId);
        if (source) {
            if (data instanceof Object &&
                !Array.isArray(data) &&
                data.type === "FeatureCollection") {
                source.setData(data);
            }
            else if (Array.isArray(data)) {
                source.setData(featureCollection(data));
            }
            else {
                console.warn(`Data for source id ${sourceId} could not be updated. Data:`, data);
            }
        }
    }
    /**
     * Adds uncomfirmed FloorData of ground, rooms and outlines to the map.
     * Will overwrite the last added floor data unless confirmed!
     * @param floorData
     * @param campusKey unless given, will be set automatically based on which campus you are currently looking at
     */
    addTemporaryFloorData(floorData, campusKey) {
        this._cuGeoJSONEditor.addTemporaryFloorData(floorData, campusKey);
    }
    confirmTemporaryFloorData() {
        this._cuGeoJSONEditor.confirmTemporaryFloorData();
    }
    resetTemporaryFloorData() {
        this._cuGeoJSONEditor.resetTemporaryFloorData();
    }
    /**
     * Find the corresponding Ground, Room, Floor, Outline polygon with the coordinates and level
     * @param coordinates
     * @param level - Level 0 is always the ground floor, no matter if its called different in reality (e.g.: Level 1 is the ground floor)
     * @param sourceId
     * @returns
     */
    getPolygonForCoordinates(coordinates, level, sourceId = this.cuSourceIds.indoorGeoJSONs.rooms) {
        const features = this.getQueriedFeatures({ sourceId, level });
        const polygonsWithCoordinates = [];
        const hasId = (id) => {
            for (let i = 0; i < polygonsWithCoordinates.length; i++) {
                if (polygonsWithCoordinates[i].id === id) {
                    return true;
                }
            }
            return false;
        };
        try {
            features.forEach((f) => {
                if (
                //@ts-ignore
                booleanPointInPolygon(coordinates, f)) {
                    // wenn ID vorhanden, prüfen ob diese nicht bereits gepusht worden ist
                    // wegen verschiedenen maptiles gibt mapbox manchmal öfter das
                    // gleiche Feature zurück
                    if ((f.id != null && !hasId(f.id)) || f.id == null) {
                        polygonsWithCoordinates.push(f);
                    }
                }
            });
            return polygonsWithCoordinates;
        }
        catch (e) {
            console.error("Something went wrong when trying to receive the Feature", e);
            return [];
        }
    }
    /**
     * Find the corresponding Room, Outline, Ground by a given property name and the value it should be
     * @param propertyName
     */
    getPolygonByProperty(propertyName, propertyValue, sourceId = this.cuSourceIds.indoorGeoJSONs.rooms) {
        return this.getQueriedFeatures({
            sourceId,
            customProperty: { key: propertyName, value: propertyValue },
        });
    }
    getQueriedFeatures({ sourceId, level, customProperty, }) {
        const polygonFilter = [
            "any",
            ["==", ["geometry-type"], "Polygon"],
            ["==", ["geometry-type"], "MultiPolygon"],
        ];
        const featureFilters = ["all", polygonFilter];
        if (level != null) {
            featureFilters.push(["==", ["get", "level"], level]);
        }
        if (customProperty != null) {
            featureFilters.push([
                "==",
                ["get", customProperty.key],
                customProperty.value,
            ]);
        }
        return this.querySourceFeatures(sourceId, {
            // filter: featureFilters as FilterSpecification, # Needed for MapLibre 3.0.*
            filter: featureFilters,
            validate: false,
            sourceLayer: "",
        });
    }
    /**
     * Sets the 3D Mode to the Room Features
     * @param value
     */
    setPolygons3dProperty(value) {
        // This is possible to fail, when those Layers don't exist yet. If it fails -> catch it.
        // Bug was experienced in the Graph-Interface, a second call was added, when GeoJsons are definitely ready.
        // So everything should work #YOLO.
        try {
            this.setPaintProperty(this.cuLayerIds.indoorGeoJSONs.rooms, "fill-extrusion-height", value ? getIndoorGeoJSONProperties().rooms.height : 0.025);
            this.setPaintProperty(this.cuLayerIds.indoorGeoJSONs.glassRooms, "fill-extrusion-height", value ? getIndoorGeoJSONProperties().rooms.glassHeight : 0.025);
        }
        catch (e) {
            console.warn("Error in setPolygons3dProperty - It looks like those layers are not initialized yet.", e);
        }
    }
    /*
     * @override
     */
    // public flyTo(options: FlyToOptions, eventData?: any) {
    //   // return this;
    //   console.log(
    //     "ICH BIN IN DER ÜBERSCHREIBENDEN MEHTODE NAMENS FLY TO RTAIMUND"
    //   );
    //   this.scrollZoom.disable();
    //   super.flyTo(options, eventData);
    //   setTimeout(() => {
    //     console.log("helfende kommentare helfen.");
    //     this.scrollZoom.enable();
    //   }, 3000);
    //   return this;
    // }
    /**
     * Same as easeTo but can handle multiple calls within seconds
     */
    stableEaseTo(options) {
        var _a;
        this.scrollZoom.disable();
        this.dragPan.disable();
        console.log("DISABLED ZOOM");
        clearTimeout(this.easeToTimer);
        //@ts-ignore
        this.easeToTimer = setTimeout(() => {
            try {
                this.easeTo(options);
            }
            catch (e) {
                console.error("stableEaseTo got unfitting parameters", e);
            }
        }, 150);
        // console.clear();
        // console.log(options);
        setTimeout(() => {
            console.log("RE-ENABLED ZOOM");
            this.scrollZoom.enable();
            this.dragPan.enable();
        }, ((_a = options.duration) !== null && _a !== void 0 ? _a : 300) + 100);
    }
    /**
     * Hides every mapbox-sourced layer except the passed one
     * @param sourceId
     */
    showMapTide(sourceId) {
        let layers = this.getStyle().layers;
        let mapTideIds;
        if (layers) {
            mapTideIds = layers
                .filter((l) => l.source && l.source.includes("mapbox://"))
                .map((l) => l.id);
            mapTideIds.forEach((name) => {
                this.setLayoutProperty(name, "visibility", "none");
            });
            if (sourceId.length) {
                if (mapTideIds.find((mt) => mt === sourceId)) {
                    this.setLayoutProperty(sourceId, "visibility", "visible");
                }
                else {
                    console.error(`No map tide with the name: "${sourceId}" found.`);
                }
            }
        }
        else {
            console.warn("No MapTides");
        }
    }
    /**
     * @deprecated
     * Set a specific Style for the map. The
     * URLs usually start with "mapbox://styles/mapbox/[styleid]"
     */
    setStyle(__, _) {
        // super.setStyle(style, options);
        //super.setStyle(mapLibreStyle ?? style, options);
        this.runMapDependends();
        //this._cuDataManager.runPiDataDependends();
        return this;
    }
    /** Loads and Adds an image to the map. For usage in symbol layers */
    async loadAndAddImage(url, name, options = {
        // The two (blue) columns of pixels that can be stretched horizontally:
        //   - the pixels between x: 25 and x: 55 can be stretched
        //   - the pixels between x: 85 and x: 115 can be stretched.
        // @ts-ignore
        stretchX: [[20, 80]],
        // The one (red) row of pixels that can be stretched vertically:
        //   - the pixels between y: 25 and y: 100 can be stretched
        stretchY: [[20, 80]],
        // This part of the image that can contain text ([x1, y1, x2, y2]):
        content: [0, 0, 100, 100],
        // This is a high-dpi image:
        pixelRatio: 2,
    }) {
        let resolver;
        let rejector;
        const promise = new Promise((resolve, reject) => {
            resolver = resolve;
            rejector = reject;
        });
        const addImage = (error, image) => {
            const result = error
                ? null
                : image;
            // if all images are loaded, call the callback
            if (result) {
                if (this.hasImage(name)) {
                    this.removeImage(name);
                }
                this.addImage(name, result, options);
                resolver(image);
            }
            rejector(error);
        };
        this.loadImage(url, addImage);
        return promise;
    }
    /** Actually start rendering the map */
    activateMap() {
        if (this.piData && this.piData.produktinstanz.mapboxAccessToken) {
            const campusCenter = this.campusCollection.getCurrentCampus().center;
            const center = [campusCenter.lng, campusCenter.lat];
            // set the campuscenter as the center, if the user did not intentionally pass
            // the center coordinates
            if (!this.initSettings.center) {
                this.setZoom(16);
                this.setCenter(center);
            }
        }
        else {
            console.error("Could not activate map due to missing Props", this.piData);
        }
    }
}
